import {
    DownloadButtonData,
    NestedDownloadData,
} from "@nova/component-export-download-buttons-hover"
import { useAppSelector } from "integrations/redux/hooks"
import { selectSelectedLayerIds } from "panels/content/side-panel/slice.new"
import { selectLiveVideoFlightById } from "features/entity-caching/flights/selectors"
import { stageKml } from "features/modals/ExportLayers/module"
import { downloadUri, downloadUriSameOrigin } from "tools/browser/download"
import { Coordinate3D, Licenses } from "@nova/constants"
import type { EventModel } from "@nova/db-table-live-video"
import { useCallback } from "react"
import { coordinateSystems } from "plugins/PDFv2/PDFAnnotations/PDFCoordinateNotations"
import { liveVideoFlightPathTransformer } from "integrations/geojson/transformers/flightpath"
import { config } from "@nova/configuration/browser"
import { selectActivatedFire } from "features/map/control/selectors"
import { getLiveVideoFlightTransformer } from "tools/transformers/getters"
import { liveVideoTranscodedFilePath } from "@nova/util-s3"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { fetchTelemetryEvents } from "tools/telemetry/fetchTelemetryEvents"

const fetchLiveVideoEvents = async ({
    orgId,
    projectId,
    flightId,
}: {
    orgId: string
    projectId: string
    flightId: string
}) => {
    const { events } = await fetchTelemetryEvents({ orgId, projectId, id: flightId })

    const sorted = events
        .filter(
            (event): event is Extract<EventModel, { type: "liveVideo" }> =>
                event.type === "liveVideo"
        )
        .sort((a, b) => a.timestamp - b.timestamp)

    return sorted
        .filter((event, i) => event.timestamp !== sorted[i - 1]?.timestamp)
        .map(
            (frame) =>
                ({
                    longitude: frame.longitude,
                    latitude: frame.latitude,
                    altitude: frame.height ?? frame.elevation,
                }) satisfies Coordinate3D
        )
}

export const useLiveVideoDownloadButtonData = (): DownloadButtonData => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const project = useAppSelector(selectActivatedFire)
    const selectedLayerId = useAppSelector(selectSelectedLayerIds).at(0)
    const id = selectedLayerId || ""
    const flight = useAppSelector(selectLiveVideoFlightById(id))
    const { organizationSettingCRS } = config.use("organizationSettingCRS")

    const getFilename = useCallback(
        (end: string) => `${project?.name ? `${project?.name} ` : ""}${flight.name} - ${end}`,
        [project?.name, flight]
    )

    if (!flight.recordingPrefix) return { exportOptions: {} }

    const downloadButtonData: NestedDownloadData = {}

    const videoOptions: NestedDownloadData = {}

    if (flight.mp4Created && flight.recordingPrefix && !!project && !!workspaceId) {
        const prefix = flight.recordingPrefix
        const downloadVideo = async () => {
            const s3Key = liveVideoTranscodedFilePath(workspaceId, project.id, flight.id, prefix)
            const url = await fileManagementApi.getDownloadUrl(s3Key)

            window.open(url, "_blank")
        }

        videoOptions.MP4 = {
            callback: downloadVideo,
            restrictedLicenses: [Licenses.Lite],
        }
    }

    const flightPathOptions: NestedDownloadData = {}

    if (!!flight.recordingPrefix && !flight.isMobile && !!project && !!workspaceId) {
        const eventsQuery = { orgId: workspaceId, projectId: project.id, flightId: flight.id }
        const downloadFlightPathKML = async () => {
            const events = await fetchLiveVideoEvents(eventsQuery)
            const kml = getLiveVideoFlightTransformer(flight.id, events)?.flightpathKML()
            if (!kml) return console.warn("Could not generate live video kml")

            const files: { blob: Blob; name: string }[] = []
            stageKml(kml, flight.name, files)

            const url = URL.createObjectURL(files[0].blob)
            downloadUriSameOrigin(files[0].name, url)
        }

        const downloadFlightpathShp = async () => {
            const res = await fetch(
                "/api/ogr2ogr?crs=" +
                    coordinateSystems[organizationSettingCRS].code +
                    "&type=" +
                    "Flightpath",
                {
                    method: "POST",
                    body: new File(
                        [
                            new Blob([
                                JSON.stringify(
                                    liveVideoFlightPathTransformer(
                                        await fetchLiveVideoEvents(eventsQuery),
                                        coordinateSystems[organizationSettingCRS].proj4
                                    )
                                ),
                            ]),
                        ],
                        "input.geojson"
                    ),
                }
            )
            const blob = await res.blob()
            await downloadUri(getFilename("Flightpath.zip"), URL.createObjectURL(blob))
        }

        flightPathOptions.KML = {
            callback: downloadFlightPathKML,
            restrictedLicenses: [Licenses.Lite],
        }

        flightPathOptions.SHP = {
            callback: downloadFlightpathShp,
            restrictedLicenses: [Licenses.Lite],
        }
    }

    if (Object.keys(videoOptions).length > 0) downloadButtonData.Video = videoOptions
    if (Object.keys(flightPathOptions).length > 0)
        downloadButtonData["Flight Path"] = flightPathOptions

    return { exportOptions: downloadButtonData }
}
